import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, ListBullets, Trash, Fire, Sparkle, Folder, FolderOpen, FolderPlus, CaretRight, CaretDown, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Modal, Field, ModalFooter } from "./Contacts";
import { WarmupWaveDialog } from "../components/WarmupWaveDialog";
import { AIListBuilderDialog } from "../components/AIListBuilderDialog";
import { BatchAllDialog } from "../components/BatchAllDialog";

export default function ListsPage() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [warmupOpen, setWarmupOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [newFolder, setNewFolder] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("list-folders-collapsed") || "[]")); } catch { return new Set(); }
  });

  const load = () => Promise.all([api.listLists().then(setItems), api.listListGroups().then(setGroups)]);
  useEffect(() => { load(); }, []);

  const toggle = (gid) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(gid) ? next.delete(gid) : next.add(gid);
    localStorage.setItem("list-folders-collapsed", JSON.stringify([...next]));
    return next;
  });

  const byGroup = useMemo(() => {
    const m = {};
    items.forEach(l => { if (l.group_id) (m[l.group_id] = m[l.group_id] || []).push(l); });
    const batchNo = (l) => { const mm = /Batch (\d+)/.exec(l.name); return mm ? parseInt(mm[1], 10) : null; };
    Object.values(m).forEach(arr => arr.sort((a, b) => {
      const na = batchNo(a), nb = batchNo(b);
      if (na !== null && nb !== null) return na - nb;
      return (a.created_at || "").localeCompare(b.created_at || "");
    }));
    return m;
  }, [items]);
  const ungrouped = useMemo(() => items.filter(l => !l.group_id), [items]);

  const moveList = async (id, gid) => {
    await api.updateList(id, { group_id: gid || null });
    toast.success(gid ? "Moved into folder" : "Removed from folder");
    load();
  };
  const renameFolder = async (g) => {
    const name = window.prompt("Folder name", g.name);
    if (!name || !name.trim() || name.trim() === g.name) return;
    await api.renameListGroup(g.id, name.trim());
    load();
  };
  const deleteFolder = async (g) => {
    if (!window.confirm(`Delete folder "${g.name}"? The lists inside are kept and just ungrouped.`)) return;
    await api.deleteListGroup(g.id);
    toast.success("Folder deleted — lists kept");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Lists</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Static, curated groups of contacts. Use folders to keep waves tidy.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-batch-all" onClick={() => setBatchOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-sky-200 text-sky-700 hover:bg-sky-50"><ListBullets size={14}/> Batch all contacts</button>
          <button data-testid="btn-ai-list" onClick={() => setAiOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50"><Sparkle size={14}/> Build list with AI</button>
          <button data-testid="btn-warmup-wave" onClick={() => setWarmupOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-orange-200 text-orange-700 hover:bg-orange-50"><Fire size={14}/> Build warm-up wave</button>
          <button data-testid="btn-new-list-folder" onClick={() => setNewFolder(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-700 hover:border-zinc-400"><FolderPlus size={14}/> New folder</button>
          <button data-testid="btn-new-list" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"><Plus size={14}/> New list</button>
        </div>
      </div>

      {groups.map(g => {
        const members = byGroup[g.id] || [];
        const totalContacts = members.reduce((s, l) => s + (l.member_count || 0), 0);
        const sentCount = members.filter(l => l.sent_status === "sent").length;
        const nextUpId = members.find(l => !l.sent_status)?.id;
        const isCollapsed = collapsed.has(g.id);
        return (
          <div key={g.id} className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <div data-testid={`list-folder-row-${g.name}`} onClick={() => toggle(g.id)}
              className="px-4 py-2.5 bg-zinc-50/80 hover:bg-zinc-100/80 cursor-pointer flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 font-medium text-zinc-900 text-sm">
                {isCollapsed ? <CaretRight size={12} className="text-zinc-400"/> : <CaretDown size={12} className="text-zinc-400"/>}
                {isCollapsed ? <Folder size={15} className="text-sky-600" weight="fill"/> : <FolderOpen size={15} className="text-sky-600" weight="fill"/>}
                {g.name}
                <span className="text-[11px] font-normal text-zinc-500">{members.length} list{members.length === 1 ? "" : "s"} · {totalContacts.toLocaleString()} contacts</span>
                {sentCount > 0 && (
                  <span data-testid={`list-folder-sent-progress-${g.id}`} className={`text-[11px] font-normal px-1.5 py-0.5 rounded-full ${sentCount === members.length ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-sky-50 text-sky-700 border border-sky-200"}`}>
                    {sentCount === members.length ? "all sent ✓" : `${sentCount} of ${members.length} sent`}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button data-testid={`list-folder-rename-${g.id}`} onClick={() => renameFolder(g)} title="Rename folder" className="text-zinc-400 hover:text-zinc-900"><PencilSimple size={14}/></button>
                <button data-testid={`list-folder-delete-${g.id}`} onClick={() => deleteFolder(g)} title="Delete folder (keeps lists)" className="text-zinc-400 hover:text-rose-700"><Trash size={14}/></button>
              </div>
            </div>
            {!isCollapsed && (
              <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {members.map(l => <ListCard key={l.id} l={l} groups={groups} onMove={moveList} onDeleted={load} nextUp={l.id === nextUpId}/>)}
                {members.length === 0 && <div className="col-span-full text-xs text-zinc-400 px-1 py-2">Empty folder — move lists here with the folder dropdown on any list.</div>}
              </div>
            )}
          </div>
        );
      })}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {ungrouped.map(l => <ListCard key={l.id} l={l} groups={groups} onMove={moveList} onDeleted={load}/>)}
        {items.length === 0 && groups.length === 0 && (
          <div className="col-span-full border border-dashed border-zinc-200 rounded-lg p-10 text-center text-sm text-zinc-500">No lists yet. Create one to start curating audiences.</div>
        )}
      </div>

      {open && <NewListModal groups={groups} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }}/>}
      {batchOpen && <BatchAllDialog onClose={() => setBatchOpen(false)} onSaved={() => { setBatchOpen(false); load(); }}/>}
      {newFolder && <NewListFolderModal onClose={() => setNewFolder(false)} onSaved={() => { setNewFolder(false); load(); }}/>}
      {warmupOpen && <WarmupWaveDialog onClose={() => setWarmupOpen(false)} onSaved={() => { setWarmupOpen(false); load(); }}/>}
      {aiOpen && <AIListBuilderDialog onClose={() => setAiOpen(false)} onSaved={() => { setAiOpen(false); load(); }}/>}
    </div>
  );
}

function ListCard({ l, groups, onMove, onDeleted, nextUp = false }) {
  return (
    <div className={`rounded-lg border bg-white p-4 transition-colors ${nextUp ? "border-amber-300 ring-1 ring-amber-200 hover:bg-amber-50/30" : "border-zinc-200 hover:bg-zinc-50/40"}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-zinc-100 flex items-center justify-center"><ListBullets size={14}/></div>
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Link to={`/lists/${l.id}`} className="font-medium text-zinc-900 hover:underline" data-testid={`list-${l.name}`}>{l.name}</Link>
              {l.sent_status === "sent" && (
                <span data-testid={`list-sent-badge-${l.id}`} title={`Sent via "${l.sent_campaign}"${l.sent_at ? ` on ${l.sent_at.slice(0, 10)}` : ""}`}
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Sent ✓{l.sent_at ? ` ${l.sent_at.slice(5, 10)}` : ""}
                </span>
              )}
              {l.sent_status === "sending" && (
                <span data-testid={`list-sending-badge-${l.id}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 animate-pulse">Sending…</span>
              )}
              {nextUp && !l.sent_status && (
                <span data-testid={`list-nextup-badge-${l.id}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-300 font-medium">Next up</span>
              )}
            </div>
            <div className="text-[11px] text-zinc-500">{l.member_count} members</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid={`list-folder-select-${l.id}`} value={l.group_id || ""} onChange={(e) => onMove(l.id, e.target.value)} title="Move to folder"
            className="h-6 max-w-[110px] rounded border border-zinc-200 bg-white px-1 text-[11px] text-zinc-600">
            <option value="">No folder</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <button onClick={async () => { if(window.confirm(`Delete list ${l.name}?`)) { await api.deleteList(l.id); onDeleted(); toast.success("Deleted"); }}} className="text-zinc-400 hover:text-rose-700"><Trash size={14}/></button>
        </div>
      </div>
      {l.description && <p className="text-xs text-zinc-500 mt-2">{l.description}</p>}
    </div>
  );
}

function NewListFolderModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const submit = async () => {
    await api.createListGroup(name.trim());
    toast.success("Folder created");
    onSaved();
  };
  return (
    <Modal title="New list folder" onClose={onClose}>
      <Field label="Folder name"><input data-testid="list-folder-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && submit()} className="modal-input" placeholder="MediPICs waves"/></Field>
      <p className="text-xs text-zinc-500 -mt-1 mb-2">Group related lists (e.g. all warm-up waves of one campaign) under a single header.</p>
      <ModalFooter onClose={onClose}>
        <button data-testid="list-folder-create" disabled={!name.trim()} onClick={submit} className="modal-btn-primary">Create</button>
      </ModalFooter>
    </Modal>
  );
}

function NewListModal({ groups, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [groupId, setGroupId] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const submit = async () => {
    let gid = groupId === "__new__" ? null : (groupId || null);
    if (groupId === "__new__" && newFolderName.trim()) {
      const g = await api.createListGroup(newFolderName.trim());
      gid = g.id;
    }
    await api.createList({ name, description: desc, contact_ids: [], group_id: gid });
    toast.success("Created");
    onSaved();
  };
  return (
    <Modal title="New list" onClose={onClose}>
      <Field label="Name"><input data-testid="list-name" value={name} onChange={(e)=>setName(e.target.value)} className="modal-input"/></Field>
      <div className="mt-3"><Field label="Description"><input data-testid="list-desc" value={desc} onChange={(e)=>setDesc(e.target.value)} className="modal-input"/></Field></div>
      <div className="mt-3">
        <Field label="Folder (optional)">
          <select data-testid="list-folder-picker" value={groupId} onChange={(e) => setGroupId(e.target.value)} className="modal-input">
            <option value="">No folder</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            <option value="__new__">＋ New folder…</option>
          </select>
        </Field>
      </div>
      {groupId === "__new__" && (
        <div className="mt-3"><Field label="New folder name"><input data-testid="list-new-folder-name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} className="modal-input" placeholder="MediPICs waves"/></Field></div>
      )}
      <ModalFooter onClose={onClose}>
        <button data-testid="list-save" disabled={!name || (groupId === "__new__" && !newFolderName.trim())} onClick={submit} className="modal-btn-primary">Create</button>
      </ModalFooter>
    </Modal>
  );
}
